import { Link, NavLink, useLocation } from 'react-router-dom'
// The canonical haevi mark, shared with the console rather than redrawn here —
// a second copy is how the two apps drift apart. Its export name is historical;
// see the component's own header.
import HaeviLogo from '@console/components/shared/PonponLogo'
import { useAuth } from '../contexts/AuthContext'
import { useSparks } from '../hooks/useSparks'
import { useSubscription } from '../hooks/useSubscription'

const MARKETING_NAV = [
  { label: 'Templates', href: '/templates' },
  { label: 'Pricing',   href: '/pricing' },
]

const APP_NAV = [
  { label: 'Workspace', href: '/dashboard' },
  { label: 'Templates', href: '/templates' },
  { label: 'Cast',      href: '/actors' },
]

export function Header() {
  const { session, signOut } = useAuth()
  const location = useLocation()
  const isLoggedIn = !!session
  const nav = isLoggedIn ? APP_NAV : MARKETING_NAV

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-bg/70 border-b border-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
        {/* Wordmark */}
        <Link to="/" className="group flex items-center gap-2.5">
          <Logo />
          <span
            className="text-[1.6rem] text-text leading-none"
            style={{
              fontFamily: 'var(--font-sans)',
              fontVariationSettings: '"wdth" 92, "opsz" 96',
              fontWeight: 700,
              letterSpacing: '-0.035em',
            }}
          >
            haevi
          </span>
          <span className="hidden sm:inline-flex items-center gap-1.5 ml-1 t-mono-caps text-text-3">
            <span className="live-dot" />
            <span>online</span>
          </span>
        </Link>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {nav.map(item => {
            const active = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href))
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={`px-3 py-1.5 rounded-sm text-[0.92rem] transition-colors ${
                  active
                    ? 'text-text bg-white/[0.04]'
                    : 'text-text-2 hover:text-text hover:bg-white/[0.03]'
                }`}
                style={{ letterSpacing: '-0.01em' }}
              >
                {item.label}
              </NavLink>
            )
          })}
        </nav>

        {/* Auth */}
        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <>
              <CreditBadge />
              <SubscriptionChip />
              <Link to="/" className="btn-quiet hidden sm:inline-flex">
                <span>+ New</span>
              </Link>
              <button onClick={() => signOut()} className="btn-quiet">
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" className="btn-accent" style={{ padding: '0.5rem 1rem', fontSize: '0.88rem' }}>
              Sign in →
            </Link>
          )}
        </div>
      </div>
    </header>
  )
}

/**
 * CreditBadge — always-visible credit (spark) balance in the header.
 *
 * Mirrors v1's `SparksBalance` (apps/console/src/components/layout/SparksBalance.tsx)
 * but restyled to match v2's glass-pill chrome with the acid-lime accent.
 * Falls back to `…` while loading and `0` if the request errors so the
 * pill is never empty/hidden — the indicator must be glanceable at all
 * times per v1 parity.
 *
 * Clicking is wired to `/pricing` as a placeholder until the Stripe
 * checkout flow lands; v1 opens UsageCostModal which we have not ported
 * yet. Keeping the navigation a stub avoids a dead button.
 */
function CreditBadge() {
  const { data, isLoading } = useSparks()
  const balance = data?.balance ?? 0
  const display = isLoading ? '…' : balance.toLocaleString()

  return (
    <Link
      to="/pricing"
      data-testid="credit-badge"
      title="Credits — click to top up"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-line-2 bg-bg-2/60 hover:bg-bg-2 hover:border-line-3 transition-colors"
      style={{ borderRadius: '999px' }}
    >
      <svg
        className="w-3.5 h-3.5 shrink-0 text-accent"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden
      >
        <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
      </svg>
      <span
        className="text-text text-[0.86rem] leading-none tabular-nums"
        style={{ fontWeight: 600, letterSpacing: '-0.01em' }}
      >
        {display}
      </span>
      <span className="t-mono-caps text-text-3 leading-none hidden sm:inline" style={{ fontSize: '0.6rem' }}>
        credits
      </span>
    </Link>
  )
}

/**
 * SubscriptionChip — small pill next to CreditBadge showing the active
 * subscription tier and renewal date. Hidden when:
 *   • subscription endpoint returns null (not subscribed, or feature off)
 *   • status is not "active" (canceled / past_due / incomplete users see
 *     no chip — the credit balance is the only signal)
 *
 * Clicking the chip navigates to /pricing where the "Manage subscription"
 * button opens the Stripe Customer Portal. Done via Link rather than
 * direct API call so a logged-out cached render can't accidentally
 * trigger a Portal request.
 */
function SubscriptionChip() {
  const { data } = useSubscription()
  if (!data || data.status !== 'active') return null
  return (
    <Link
      to="/pricing"
      data-testid="subscription-chip"
      title="Subscription — click to manage"
      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-accent/40 bg-accent/10 hover:bg-accent/15 transition-colors"
      style={{ borderRadius: '999px' }}
    >
      <span
        className="text-text text-[0.8rem] leading-none"
        style={{ fontWeight: 600, letterSpacing: '-0.01em' }}
      >
        {planLabel(data.plan_tier)}
      </span>
      <span
        className="t-mono-caps text-text-3 leading-none"
        style={{ fontSize: '0.6rem' }}
      >
        renews {formatRenewalDate(data.current_period_end)}
      </span>
    </Link>
  )
}

function planLabel(tier: string): string {
  switch (tier) {
    case 'hobby':
      return 'Hobby'
    case 'pro':
      return 'Pro'
    case 'studio':
      return 'Studio'
    default:
      return tier
  }
}

function formatRenewalDate(iso: string | null): string {
  if (!iso) return 'soon'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return 'soon'
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return 'soon'
  }
}

function Logo() {
  // The wordmark beside it already reads "haevi", so the mark is decorative
  // and must not be announced a second time.
  // The mark carries the brand on a page that is otherwise typographic, so it
  // is sized to be seen rather than to sit politely beside the nav. The glow
  // is the mark's own gradient, blurred behind it — it reads on the near-black
  // background without adding a second colour to the palette.
  return (
    <span aria-hidden className="relative inline-flex items-center justify-center">
      <span
        className="absolute inset-0 rounded-[12px] blur-[14px] opacity-70"
        style={{ background: 'linear-gradient(135deg, #4f46e5, #f0567a)' }}
      />
      <HaeviLogo size={38} className="relative" />
    </span>
  )
}
